import { z } from 'zod';
import { instrumentoSchema } from '../eventos.js';
import { campoSchema, respuestaEscalarSchema, } from './tipos.js';
/**
 * Definición declarativa de un formulario.
 *
 * Un formulario es **datos, no código**: una lista de preguntas, cada una con su
 * campo del catálogo de tipos, su condición de aparición y su forma de
 * presentarse. De la misma definición salen cuatro cosas que antes se escribían
 * por separado y se separaban entre sí — la UI, la validación de la respuesta,
 * el puntaje y el `Questionnaire` de FHIR.
 *
 * Hay **dos clases de condicionalidad**, y no son lo mismo:
 *
 *   1. **Entre preguntas** (`cuando`) — una pregunta aparece sólo si otra tomó
 *      cierto valor. Es la skip-logic del NMS: si el paciente no camina por su
 *      casa, no se le pregunta si sale ni si hace las compras. Se proyecta a
 *      `Questionnaire.item.enableWhen`.
 *
 *   2. **Dentro de una pregunta** (`presentacion.modo = 'arbol'`) — el valor de
 *      la pregunta se elicita con un árbol de sí/no en vez de con una lista de
 *      opciones. Es cómo Fundación administra el NMS (`nms.svg`): tres pasos
 *      binarios que resuelven a una de las cuatro anclas del instrumento. **El
 *      árbol es cómo se pregunta, no qué se registra**: la respuesta sigue
 *      siendo una opción del campo, y eso es lo que va al
 *      `QuestionnaireResponse`. Registrar los nodos del árbol como ítems
 *      convertiría al NMS en un instrumento local y perdería la comparabilidad
 *      con la literatura, que es justamente lo que se ganó al adoptar el
 *      original (decisión 2026-08-24, enmienda ADR-008).
 */
// ─────────────────────────────────────────────────────────────────────────────
// Condición entre preguntas
// ─────────────────────────────────────────────────────────────────────────────
export const operadorCondicionSchema = z.enum(['igual', 'distinto', 'mayor-o-igual', 'menor-que']);
export const condicionSchema = z.strictObject({
    /** `id` de la pregunta de la que depende. Tiene que estar antes en la lista. */
    pregunta: z.string().min(1),
    operador: operadorCondicionSchema,
    /** Escalar: una condición no se compara contra las filas de un grupo. */
    valor: respuestaEscalarSchema,
});
/**
 * Evalúa una condición contra lo respondido.
 *
 * Sin respuesta la condición **no se cumple**: una pregunta condicionada no
 * aparece hasta que su condición esté resuelta. Es lo que hace que el recorrido
 * avance de a una pregunta por pantalla sin mostrar la siguiente antes de
 * tiempo.
 */
export function condicionSeCumple(condicion, respuestas) {
    const valor = respuestas[condicion.pregunta];
    if (valor === undefined)
        return false;
    switch (condicion.operador) {
        case 'igual':
            return valor === condicion.valor;
        case 'distinto':
            return valor !== condicion.valor;
        case 'mayor-o-igual':
            return typeof valor === 'number' && valor >= Number(condicion.valor);
        case 'menor-que':
            return typeof valor === 'number' && valor < Number(condicion.valor);
    }
}
export const nodoDeArbolSchema = z.lazy(() => z.discriminatedUnion('clase', [
    z.strictObject({ clase: z.literal('resultado'), opcion: z.string().min(1) }),
    z.strictObject({
        clase: z.literal('pregunta'),
        texto: z.string().min(1),
        si: nodoDeArbolSchema,
        no: nodoDeArbolSchema,
    }),
]));
export const presentacionSchema = z.union([
    z.strictObject({ modo: z.literal('lista') }),
    /** Escala de caritas del P-05, con las anclas del campo a los costados. */
    z.strictObject({ modo: z.literal('emoji') }),
    /** El termómetro del EQ-5D: un valor 0–100 sobre una regla vertical. */
    z.strictObject({ modo: z.literal('termometro') }),
    z.strictObject({ modo: z.literal('arbol'), arbol: nodoDeArbolSchema }),
    /**
     * El control propio del tipo (E3.25): el numérico con su unidad, el selector
     * de fecha, el buscador del catálogo, las filas del grupo. Los cuatro tipos
     * nuevos se presentan así y sólo así: una cantidad con unidad no es una lista
     * de botones, y una lista de veinte drogas tampoco.
     */
    z.strictObject({ modo: z.literal('propia') }),
]);
// ─────────────────────────────────────────────────────────────────────────────
// Pregunta y formulario
// ─────────────────────────────────────────────────────────────────────────────
/**
 * `id` de pregunta. Es el `linkId` del `Questionnaire`: estable para siempre,
 * porque un `QuestionnaireResponse` viejo lo referencia.
 */
const idDePregunta = z
    .string()
    .min(1)
    .max(30)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'el id de una pregunta es kebab-case');
export const preguntaSchema = z.strictObject({
    id: idDePregunta,
    /** Redacción oficial del instrumento. No se retoca: se cita. */
    texto: z.string().min(1),
    campo: campoSchema,
    presentacion: presentacionSchema,
    ayuda: z.string().min(1).optional(),
    /** Condiciones que tienen que cumplirse todas (`enableBehavior: all`). */
    cuando: z.array(condicionSchema).optional(),
    /**
     * Si el paciente puede saltearla ("Preguntame más tarde", P-05). Las preguntas
     * de un instrumento no son opcionales: saltear una deja la escala parcial, que
     * es un estado distinto y con su propio evento.
     */
    opcional: z.boolean(),
});
/**
 * Las tres clases de formulario.
 *
 *   - `escala`: uno de los cuatro instrumentos, con su puntaje. Es lo único que
 *     enciende el semáforo (R-08).
 *   - `microcheck`: la batería corta del check-in. No puntúa.
 *   - `cuestionario` (E3.25, D-01): las preguntas "extra" que el protocolo de
 *     Fundación pide en cada hito además de las escalas (medicación,
 *     reinternaciones, reintervenciones, autonomía, reintegración), y la
 *     medicación al egreso que se carga en el alta. **No es un instrumento y no
 *     puntúa**: son hechos y respuestas que el equipo lee, y ninguno mueve el
 *     semáforo. No es un microcheck porque no es el contacto del check-in: cuelga
 *     del ítem de la evaluación y se responde después de las escalas.
 */
export const claseDeFormularioSchema = z.enum(['escala', 'microcheck', 'cuestionario']);
export const formularioSchema = z.strictObject({
    /** Coincide con el `id` del `Questionnaire` en el store. */
    id: z.string().min(1).max(30),
    clase: claseDeFormularioSchema,
    titulo: z.string().min(1),
    /** Versión del `Questionnaire`. Semver; cambia con la redacción o los ítems. */
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** `active` congela la redacción; `draft` avisa que todavía puede cambiar. */
    estado: z.enum(['draft', 'active']),
    /** Presente sólo en las escalas: es lo que ata el formulario a su scoring. */
    instrumento: instrumentoSchema.optional(),
    preguntas: z.array(preguntaSchema).min(1),
});
// ─────────────────────────────────────────────────────────────────────────────
// Coherencia de una definición
// ─────────────────────────────────────────────────────────────────────────────
/** Recorre el árbol y devuelve los códigos de opción a los que puede llegar. */
export function resultadosDelArbol(nodo) {
    if (nodo.clase === 'resultado')
        return [nodo.opcion];
    return [...resultadosDelArbol(nodo.si), ...resultadosDelArbol(nodo.no)];
}
/**
 * Problemas estructurales de una definición.
 *
 * No es validación de entrada: es la red que atrapa una definición mal escrita
 * **antes** de que llegue a un paciente. Un árbol que resuelve a una opción que
 * no existe se ve como una pantalla que no avanza; una condición que apunta a
 * una pregunta posterior, como una pregunta que nunca aparece. Las dos cosas se
 * detectan leyendo la definición, así que hay un test que las lee.
 */
export function problemasDeLaDefinicion(formulario) {
    const problemas = [];
    const vistas = new Set();
    const grupos = new Set();
    if (formulario.clase === 'escala' && !formulario.instrumento) {
        problemas.push('una escala tiene que declarar su instrumento');
    }
    if (formulario.clase !== 'escala' && formulario.instrumento) {
        problemas.push(`un ${formulario.clase} no es un instrumento con puntaje propio`);
    }
    for (const pregunta of formulario.preguntas) {
        if (vistas.has(pregunta.id))
            problemas.push(`${pregunta.id}: id repetido`);
        vistas.add(pregunta.id);
        problemas.push(...problemasDeLasCondiciones(pregunta, vistas, grupos), ...problemasDeLaPresentacion(pregunta), ...problemasDelCampo(pregunta));
        if (pregunta.campo.tipo === 'grupo')
            grupos.add(pregunta.id);
    }
    return problemas;
}
/**
 * Una condición apunta a una pregunta anterior, y nunca a un grupo: las filas
 * de un grupo no son un valor contra el que se pueda comparar.
 */
function problemasDeLasCondiciones(pregunta, anteriores, grupos) {
    return (pregunta.cuando ?? []).flatMap((condicion) => {
        if (!anteriores.has(condicion.pregunta)) {
            return [
                `${pregunta.id}: depende de "${condicion.pregunta}", que no está antes en el formulario`,
            ];
        }
        if (grupos.has(condicion.pregunta)) {
            return [`${pregunta.id}: una condición no puede depender de un grupo`];
        }
        return [];
    });
}
/** Los tipos que se presentan con su control propio, y sólo con él. */
const CON_CONTROL_PROPIO = new Set([
    'numero',
    'fecha',
    'catalogo',
    'grupo',
]);
/**
 * Lo que el schema no puede ver de un campo: que el rango esté al derecho y que
 * las filas de un grupo no repitan una pregunta.
 */
function problemasDelCampo(pregunta) {
    const { campo } = pregunta;
    if (campo.tipo === 'numero' && campo.minimo > campo.maximo) {
        return [`${pregunta.id}: el mínimo es mayor que el máximo`];
    }
    if (campo.tipo === 'grupo')
        return problemasDelGrupo(pregunta.id, campo);
    return [];
}
function problemasDelGrupo(id, campo) {
    const problemas = [];
    if (campo.minimo > campo.maximo)
        problemas.push(`${id}: el mínimo de filas es mayor que el máximo`);
    const vistas = new Set();
    for (const pregunta of campo.preguntas) {
        if (vistas.has(pregunta.id))
            problemas.push(`${id}.${pregunta.id}: id repetido en la fila`);
        vistas.add(pregunta.id);
        if (pregunta.campo.tipo === 'numero' && pregunta.campo.minimo > pregunta.campo.maximo) {
            problemas.push(`${id}.${pregunta.id}: el mínimo es mayor que el máximo`);
        }
    }
    return problemas;
}
function problemasDeLaPresentacion(pregunta) {
    const { campo, presentacion } = pregunta;
    if (CON_CONTROL_PROPIO.has(campo.tipo) !== (presentacion.modo === 'propia')) {
        return [
            CON_CONTROL_PROPIO.has(campo.tipo)
                ? `${pregunta.id}: un campo "${campo.tipo}" se presenta con su control propio`
                : `${pregunta.id}: el control propio es de los campos numero, fecha, catalogo y grupo`,
        ];
    }
    if (presentacion.modo === 'arbol') {
        if (campo.tipo !== 'opcion') {
            return [`${pregunta.id}: el árbol resuelve a una opción y el campo es "${campo.tipo}"`];
        }
        return problemasDelArbol(pregunta.id, campo, presentacion.arbol);
    }
    if (presentacion.modo === 'termometro' && campo.tipo !== 'puntaje') {
        return [`${pregunta.id}: el termómetro necesita un campo de puntaje`];
    }
    // Un puntaje se puede mostrar como botones sólo si el rango es corto. Con más
    // de once valores la pantalla deja de ser una lista y pasa a ser una grilla
    // que un adulto mayor no puede usar con el pulgar: para eso está el
    // termómetro.
    if (presentacion.modo === 'lista' && campo.tipo === 'puntaje') {
        const valores = campo.maximo - campo.minimo + 1;
        if (valores > 11) {
            return [`${pregunta.id}: ${valores} valores no entran como lista; usá el termómetro`];
        }
    }
    return [];
}
function problemasDelArbol(id, campo, arbol) {
    const problemas = [];
    const alcanzables = resultadosDelArbol(arbol);
    const codigos = campo.opciones.map((opcion) => opcion.codigo);
    for (const resultado of alcanzables) {
        if (!codigos.includes(resultado)) {
            problemas.push(`${id}: el árbol resuelve a "${resultado}", que no es una opción del campo`);
        }
    }
    for (const codigo of codigos) {
        if (!alcanzables.includes(codigo)) {
            problemas.push(`${id}: la opción "${codigo}" no es alcanzable desde el árbol`);
        }
    }
    return problemas;
}
/** Tira si la definición tiene problemas. La usan los tests y el arranque. */
export function exigirDefinicionCoherente(formulario) {
    const problemas = problemasDeLaDefinicion(formulario);
    if (problemas.length > 0) {
        throw new Error(`Definición inválida de ${formulario.id}:\n  - ${problemas.join('\n  - ')}`);
    }
    return formulario;
}
/**
 * Los catálogos de datos que el formulario nombra, en sus preguntas y en las
 * filas de sus grupos. Es lo que la API mira para saber si tiene que cargar un
 * catálogo antes de validar una respuesta: la mayoría de los formularios no
 * nombra ninguno y no tiene por qué pagar la consulta.
 */
export function catalogosDelFormulario(formulario) {
    const catalogos = new Set();
    for (const { campo } of formulario.preguntas) {
        const campos = campo.tipo === 'grupo' ? campo.preguntas.map((fila) => fila.campo) : [campo];
        for (const candidato of campos) {
            if (candidato.tipo === 'catalogo')
                catalogos.add(candidato.catalogo);
        }
    }
    return [...catalogos];
}
//# sourceMappingURL=definicion.js.map